import React, { useState, useEffect } from 'react';
import { getCsrfToken } from '../utils/auth';
import { updateDevkit } from '../utils/updateDevkit';

const channelMap = {
  'Stable': 'release',
  'Nightly': 'testing',
  'Experimental': 'sandbox',
};

const reverseChannelMap = {
  'release': 'Stable',
  'testing': 'Nightly',
  'sandbox': 'Experimental'
}

const ActionButton = ({
  children,
  host,
  app,
  variant = 'primary',
  updatingApp,
  onClick,
  disabled = false,
  version,
  build,
  setRefreshDevkit,
  updateAvailable,
  releaseChannel,
  setReleaseChannel,
  appDisplayName, // New prop for display name (e.g., "Poseidon", "Core", "Ironman")
  poseidonVersion, // Drives EXA_DEV/ build path selection
  onActiveChange, // (isActive: boolean) — fires when this card opens or starts an update
  onSendingChange, // (isSending: boolean) — fires only while an update request is in flight
  inlinePanel = false, // render the expanded panel in normal flow (for modal hosts) instead of absolutely below
  compact = false
}) => {
  const [channel, setChannel] = useState(() => reverseChannelMap[releaseChannel] || 'Stable');
  const [selectedVersion, setSelectedVersion] = useState('latest');
  const [availableUpdates, setAvailableUpdates] = useState([]);
  const [updateBody, setUpdateBody] = useState(null);

  const [isSending, setIsSending] = useState(false);
  const [confirmUpdate, setConfirmUpdate] = useState(false);
  const [activeUpdateStep, setActiveUpdateStep] = useState('');
  const [currentUpdatingDevice, setCurrentUpdatingDevice] = useState(null);
  const [updateStartedAt, setUpdateStartedAt] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [lastResult, setLastResult] = useState(null);
  const [updateProgress, setUpdateProgress] = useState(null);

  useEffect(() => {
    if (!updateStartedAt) return undefined;
    setElapsedMs(Date.now() - updateStartedAt);
    const id = setInterval(() => setElapsedMs(Date.now() - updateStartedAt), 1000);
    return () => clearInterval(id);
  }, [updateStartedAt]);

  const formatElapsed = (ms) => {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes <= 0) return null;
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const formatEta = (seconds) => {
    if (seconds == null || seconds < 0) return null;
    const totalSec = Math.max(0, Math.round(seconds));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
  };

  // Color variants for different apps
  const colorVariants = {
    orange: 'bg-orange-50 border-orange-100',
    blue: 'bg-blue-50 border-blue-100',
    green: 'bg-green-50 border-green-100',
    purple: 'bg-purple-50 border-purple-100',
  };

  const buttonVariants = {
    primary: 'bg-[#036AA9] hover:bg-[#005f95] text-white',           // Brand blue
    dark: 'bg-[#003759] hover:bg-[#002c47] text-white',           // Deep blue
    navy: 'bg-[#0E103D] hover:bg-[#05061d] text-white',           // Navy
    neutral: 'bg-[#57585B] hover:bg-[#3f3f41] text-white',           // Slate gray
    light: 'bg-[#EBEBEB] hover:bg-[#d5d5d5] text-black',           // Light gray
    ghost: 'bg-transparent hover:bg-[#036AA9]/10 text-[#036AA9]',  // Transparent brand blue
    // Optional existing variants
    success: 'bg-green-600 hover:bg-green-700 text-white',
    warning: 'bg-orange-500 hover:bg-orange-700 text-white',
  };


  const extractVersionBuild = (str, type) => {
    const regexByType = {
      ironman: /proteusIronman_(\d+\.\d+\.\d+)_(\d+)_/,
      apollocore: /ApolloCore_(\d+\.\d+\.\d+)_(\d+)_/,
      proteuscore: /ProteusCore_(\d+\.\d+\.\d+)_(\d+)_/,
    };
    const regex = regexByType[type] ?? regexByType.proteuscore;

    const match = str.match(regex);
    if (!match) return null;

    return { version: match[1], build: match[2] };
  };

  const getDeviceAvailableUpdates = async () => {
    setAvailableUpdates([])

    const params = new URLSearchParams({ channel: channelMap[channel], app });
    if (poseidonVersion) params.set('poseidon', poseidonVersion);
    const res = await fetch(`/api/available-versions?${params.toString()}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-CSRF-TOKEN': getCsrfToken()
      }
    });
    if (!res.ok) return;

    const data = await res.json();

    const versions = data.available_versions
      .map(v => extractVersionBuild(v, app))
      .filter(Boolean)
      .sort((a, b) => Number(b.build) - Number(a.build));

    setAvailableUpdates(versions);
  };

  // Use updateDevkit.js for update logic
  const sendUpdate = async () => {
    const displayName = appDisplayName || app;
    setIsSending(true);
    setCurrentUpdatingDevice(displayName);
    setActiveUpdateStep('Sending update request...');
    setUpdateStartedAt(Date.now());
    setElapsedMs(0);
    setLastResult(null);
    setUpdateProgress(null);
    let succeeded = false;
    let failureMessage = null;
    try {
      await updateDevkit({
        host,
        app,
        devkitName: displayName,
        updateBody,
        onStep: (step) => setActiveUpdateStep(step || ''),
        onProgress: (progress) => setUpdateProgress(progress),
      });
      succeeded = true;
    } catch (err) {
      failureMessage = err?.message || 'Update failed';
      console.error('Failed to update:', err);
    } finally {
      setIsSending(false);
      setUpdateStartedAt(null);
      setActiveUpdateStep('');
      setCurrentUpdatingDevice(null);
      setUpdateProgress(null);
      if (succeeded) {
        const versionStr = updateBody?.version
          ? `v${updateBody.version}${updateBody.build ? ` (${updateBody.build})` : ''}`
          : 'the latest version';
        setLastResult({
          kind: 'success',
          message: `${displayName} updated to ${versionStr}.`,
        });
      } else {
        setLastResult({
          kind: 'error',
          message: `${displayName} update failed: ${failureMessage}`,
        });
      }
      onClick && onClick();
      setRefreshDevkit((p) => !p);
    }
  };

  // Errors persist until dismissed; only success self-clears.
  useEffect(() => {
    if (lastResult?.kind !== 'success') return undefined;
    const id = setTimeout(() => {
      setLastResult((prev) => (prev?.kind === 'success' ? null : prev));
    }, 10_000);
    return () => clearTimeout(id);
  }, [lastResult]);

  // Tell the parent whenever this card becomes the "active" one (selector open
  // or update in flight) so it can dim the other ActionButtons in the group.
  useEffect(() => {
    if (!onActiveChange) return undefined;
    onActiveChange(!!(updatingApp || isSending));
    return () => onActiveChange(false);
  }, [updatingApp, isSending]);

  // Sending-only signal (unlike onActiveChange, ignores the selector being
  // open) so a modal host can refuse to close while a request is in flight.
  useEffect(() => {
    if (!onSendingChange) return undefined;
    onSendingChange(isSending);
    return () => onSendingChange(false);
  }, [isSending]);

  useEffect(() => {
    if (updatingApp) {
      getDeviceAvailableUpdates();
    }
  }, [channel, updatingApp]);

  // One-way sync from the device's stored channel. Pushing the user's pick
  // back up happens in the select's onChange only — doing it here created a
  // prop<->state feedback loop that cycled the selects on its own whenever
  // the device wasn't on Stable.
  useEffect(() => {
    if (releaseChannel && reverseChannelMap[releaseChannel]) {
      setChannel(reverseChannelMap[releaseChannel]);
    }
  }, [releaseChannel]);

  useEffect(() => {
    if (selectedVersion === 'latest') {
      if (availableUpdates.length > 0) {
        const latest = availableUpdates[0];
        setUpdateBody({ version: latest.version, build: Number(latest.build) });
      } else {
        setUpdateBody({ version: 'latest' });
      }
    } else {
      const m = selectedVersion.match(/^(?<version>[\d.]+)\s\((?<build>\d+)\)$/);
      if (m?.groups) {
        setUpdateBody({ version: m.groups.version, build: Number(m.groups.build) });
      }
    }
  }, [selectedVersion, availableUpdates]);

  // Determine card color based on variant or app name
  const getCardColor = () => {
    if (variant === 'warning') return colorVariants.orange;
    if (variant === 'success') return colorVariants.green;
    if (variant === 'secondary') return colorVariants.purple;
    return colorVariants.blue;
  };

  return (
    <>
      <div className="relative">
      {/* inlinePanel hosts (the System Update modal) provide their own chrome,
          so skip the boxed card styling there to avoid a box-in-box look. */}
      <div className={inlinePanel
        ? 'pb-3'
        : `${getCardColor()} border ${compact ? 'px-3 py-2' : 'p-3'} transition-[border-radius,border-color,box-shadow] duration-150 ease-out ${(isSending || lastResult || updatingApp) ? 'relative z-20 shadow-md rounded-t-lg border-b-0' : 'rounded-lg'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <p className={`font-medium text-gray-900 ${compact ? 'text-sm leading-tight' : ''}`}>{appDisplayName}</p>
              <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>
                {version !== 'unknown'
                  ? `v${version}${build ? ` (${build})` : ''}`
                  : 'Version unknown'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {updateAvailable && !updatingApp && (
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
            )}
            {!updatingApp ? (
              <button
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${buttonVariants[variant]}`}
                onClick={onClick}
                disabled={disabled || isSending}
              >
                {isSending ? (
                  <span className="inline-flex items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" strokeLinecap="round" />
                      <path className="opacity-75" d="M4 12a8 8 0 018-8" strokeLinecap="round" />
                    </svg>
                    Updating...
                  </span>
                ) : (
                  children || 'Update'
                )}
              </button>
            ) : !inlinePanel ? (
              <button
                onClick={() => onClick()}
                title="Cancel update"
                className="px-2 py-1 text-sm text-gray-400 hover:text-gray-700 rounded focus:outline-none"
              >
                ✕
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {(isSending || lastResult || updatingApp) && (
        <div className={`${inlinePanel
          ? 'space-y-3'
          : `${getCardColor()} absolute left-0 right-0 top-full border border-t-0 rounded-b-lg shadow-xl p-3 space-y-3 z-20`} origin-top animate-in fade-in slide-in-from-top-1 duration-150 ease-out`}>
        {/* In-progress banner: prominent, with elapsed time + slow-network hint */}
        {isSending && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <circle cx="12" cy="12" r="10" className="opacity-25" />
                  <path d="M4 12a8 8 0 018-8" className="opacity-75" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-semibold text-amber-900">
                    Updating {currentUpdatingDevice}
                  </div>
                  <div className="text-xs font-mono text-amber-700 tabular-nums">
                    {formatElapsed(elapsedMs)}
                  </div>
                </div>
                <div className="text-sm text-amber-700 mt-0.5">
                  {activeUpdateStep || 'Preparing update...'}
                </div>
                {(() => {
                  const p = updateProgress;
                  if (!p) return null;
                  const percent = typeof p.percent === 'number' ? Math.max(0, Math.min(100, p.percent)) : null;
                  const hasBytes = p.bytes_total > 0;
                  const doneStr = hasBytes ? formatBytes(p.bytes_done) : null;
                  const totalStr = hasBytes ? formatBytes(p.bytes_total) : null;
                  const throughputStr =
                    typeof p.throughput_mbps === 'number' && p.throughput_mbps > 0
                      ? `${p.throughput_mbps.toFixed(1)} MB/s`
                      : null;
                  const etaStr = formatEta(p.eta_seconds);
                  const showBar = percent != null && (hasBytes || percent > 0);
                  const detailParts = [
                    hasBytes ? `${doneStr} / ${totalStr}` : null,
                    throughputStr,
                    etaStr ? `ETA ${etaStr}` : null,
                  ].filter(Boolean);
                  if (!showBar && detailParts.length === 0) return null;
                  return (
                    <div className="mt-2 space-y-1">
                      {showBar && (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 rounded-full bg-amber-100 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-[width] duration-500 ease-out"
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                          <div className="text-xs font-mono tabular-nums text-amber-800 w-10 text-right">
                            {Math.round(percent)}%
                          </div>
                        </div>
                      )}
                      {detailParts.length > 0 && (
                        <div className="text-xs text-amber-700 tabular-nums">
                          {detailParts.join(' · ')}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {elapsedMs >= 30_000 && (
                  <div className="text-xs text-amber-600 mt-1 italic">
                    Still working — this can take several minutes on slow networks.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Persistent result banner: success auto-clears after 10s, errors stay until dismissed */}
        {!isSending && lastResult && (
          <div
            className={`rounded-lg border px-4 py-3 shadow-sm flex items-start gap-3 ${
              lastResult.kind === 'success'
                ? 'border-emerald-300 bg-emerald-50'
                : 'border-red-300 bg-red-50'
            }`}
            role={lastResult.kind === 'error' ? 'alert' : 'status'}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                lastResult.kind === 'success' ? 'bg-emerald-500' : 'bg-red-500'
              }`}
            >
              {lastResult.kind === 'success' ? (
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.5M12 16h.01M4.93 19h14.14a2 2 0 001.74-3l-7.07-12a2 2 0 00-3.48 0L3.2 16a2 2 0 001.73 3z" />
                </svg>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div
                className={`font-semibold ${
                  lastResult.kind === 'success' ? 'text-emerald-900' : 'text-red-900'
                }`}
              >
                {lastResult.kind === 'success' ? 'Update complete' : 'Update failed'}
              </div>
              <div
                className={`text-sm mt-0.5 break-words ${
                  lastResult.kind === 'success' ? 'text-emerald-800' : 'text-red-800'
                }`}
              >
                {lastResult.message}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setLastResult(null)}
              className={`flex-shrink-0 rounded p-1 text-sm leading-none focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                lastResult.kind === 'success'
                  ? 'text-emerald-700 hover:text-emerald-900 focus:ring-emerald-500'
                  : 'text-red-700 hover:text-red-900 focus:ring-red-500'
              }`}
              aria-label="Dismiss"
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {updatingApp && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex gap-2 w-full">
                <select
                  className="border rounded w-1/2 px-2 py-1 text-sm bg-white"
                  value={channel}
                  onChange={(e) => {
                    const next = e.target.value;
                    setChannel(next);
                    setSelectedVersion('latest');
                    setConfirmUpdate(false);
                    setReleaseChannel(channelMap[next]);
                  }}
                >
                  {['Stable', 'Nightly', 'Experimental'].map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>

                <select
                  className="border rounded w-1/2 px-2 py-1 text-sm bg-white"
                  value={selectedVersion}
                  onChange={(e) => setSelectedVersion(e.target.value)}
                >
                  <option value="latest">Latest version</option>
                  {availableUpdates.map((v, i) => (
                    <option key={i} value={`${v.version} (${v.build})`}>
                      {`${v.version} (${v.build})`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {!confirmUpdate ? (
              <button
                className={`w-full px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${buttonVariants[variant]}`}
                onClick={() => setConfirmUpdate(true)}
                disabled={disabled || isSending}
              >
                Update to {selectedVersion === 'latest' ? 'latest version' : selectedVersion}
              </button>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 space-y-2 animate-in fade-in duration-150">
                <p className="text-sm text-gray-700 text-center">
                  Update <strong>{appDisplayName || app}</strong> to{' '}
                  <strong>{selectedVersion === 'latest' ? 'the latest version' : selectedVersion}</strong>?
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirmUpdate(false)}
                    className="flex-1 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setConfirmUpdate(false);
                      sendUpdate();
                    }}
                    disabled={disabled || isSending}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${buttonVariants[variant]}`}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        </div>
      )}
      </div>

    </>
  );
};

// Example usage showing how it would look
const ExampleUsage = () => {
  const [updatingApp, setUpdatingApp] = useState(null);

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-4">
      <h2 className="text-xl font-semibold mb-4">Device Components</h2>

      {/* Poseidon Update */}
      <ActionButton
        app="poseidon"
        appDisplayName="Poseidon"
        version="1.2.3"
        build="456"
        variant="warning"
        updateAvailable={true}
        updatingApp={updatingApp === 'poseidon'}
        onClick={() => setUpdatingApp(updatingApp === 'poseidon' ? null : 'poseidon')}
        setRefreshDevkit={() => { }}
        releaseChannel="release"
        setReleaseChannel={() => { }}
      >
        Update
      </ActionButton>

      {/* Core Update */}
      <ActionButton
        app="core"
        appDisplayName="Core"
        version="2.0.1"
        build="789"
        variant="primary"
        updateAvailable={false}
        updatingApp={updatingApp === 'core'}
        onClick={() => setUpdatingApp(updatingApp === 'core' ? null : 'core')}
        setRefreshDevkit={() => { }}
        releaseChannel="release"
        setReleaseChannel={() => { }}
      >
        Update
      </ActionButton>

      {/* Ironman Update */}
      <ActionButton
        app="ironman"
        appDisplayName="Ironman"
        version="unknown"
        variant="secondary"
        updateAvailable={false}
        updatingApp={updatingApp === 'ironman'}
        onClick={() => setUpdatingApp(updatingApp === 'ironman' ? null : 'ironman')}
        setRefreshDevkit={() => { }}
        releaseChannel="testing"
        setReleaseChannel={() => { }}
      >
        Update
      </ActionButton>
    </div>
  );
};

export default ActionButton;